import React, { useState, useEffect } from "react";

import {
  Box,
  Typography,
  TextField,
  Grid,
  Button,
  Paper,
  InputAdornment,
  IconButton,
} from "@mui/material";

import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";

import {
  createCustomer,
  getCustomers,
  updateCustomer,
  deleteCustomer,
} from "../../api/customer";
import { handleAxiosError } from "../../config/action-config";

import { customerTable as tableDef } from "../../config/grid-columns-defs";
import ControlledTable from "../../component/ControlledTable";
import DeleteAlertDialog from "../../component/DeleteAlertDialog";
import UpdateDialog from "../../component/UpdateDialog";
import CreateDialog from "../../component/CreateDialog";
import { showAlert } from "../../actions/alert";
import { useForm, Controller } from "react-hook-form";
import BetterControlledTable from "../../component/BetterControlledTable";
import { Visibility, VisibilityOff } from "@mui/icons-material";

const emptyUpdateData = {
  vendor_name: "",
  company_name: "",
  shipping_address: "",
  billing_address: "",
  contact: "",
  postal_code: "",
  email: "",
  country: "",
  city: "",
  currency: "",
  state: "",
};

const emptyNewCustomerData = {
  email: "",
  firstname: "",
  lastname: "",
  address: "",
  contact: "",
};

export default function Customer() {
  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm();
  const user = useSelector((state) => state.user);
  const dialogAction = useSelector((state) => state.dialog);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [totalCount, setTotalCount] = useState(0);

  const [rows, setRows] = useState([]);
  const [pageNumber, setPageNumber] = useState(0);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [updateDialogOpen, setUpdateDialogeOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(-1);
  const [updateId, setUpdateId] = useState(-1);

  const [updateData, setUpdateData] = useState(emptyUpdateData);
  const [newCustomerData, setNewCustomerData] = useState(emptyNewCustomerData);

  const onNewVendorSubmit = (data) => {
    // createCustomer(newCustomerData, user.token)
    createCustomer(data, user.token)
      .then((res) => {
        dispatch(showAlert(res.data.msg));
        reset();
        setNewCustomerData(emptyNewCustomerData);
        refreshTable();
        setCreateDialogOpen(false);
      })
      .catch((err) => {
        handleAxiosError(err, dispatch);
        setCreateDialogOpen(false);
      });
  };

  function refreshTable(e) {
    e?.preventDefault();
    setCustomerTableRefreshCount((prev) => prev + 1);
  }

  useEffect(() => {
    if (!dialogAction.type) return;
    switch (dialogAction.type) {
      case "EDIT": {
        setUpdateDialogeOpen(true);
        const up = getUpdatableData(dialogAction.data);
        setUpdateData(up);
        break;
      }
      case "DELETE":
        setDeleteDialogOpen(true);
        break;
      case "VIEW":
        dispatch({ type: "" });
        navigate(`${dialogAction.data.id}`);
        break;
      default:
        break;
    }
  }, [dialogAction]);

  useEffect(() => {
    if (deleteId === -1) return;
    deleteCustomer(deleteId, user.token)
      .then((res) => {
        dispatch(showAlert(res.data.msg));
        setDeleteId(-1);
        refreshTable();
        setDeleteDialogOpen(false);
      })
      .catch((err) => {
        handleAxiosError(err, dispatch);
        setDeleteId(-1);
        setDeleteDialogOpen(false);
      });
  }, [deleteId]);

  useEffect(() => {
    if (updateId === -1) return;
    updateCustomer(updateId, updateData, user.token)
      .then((res) => {
        dispatch(showAlert(res.data.msg));
        setUpdateId(-1);
        setUpdateData(emptyUpdateData);
        refreshTable();
        setUpdateDialogeOpen(false);
      })
      .catch((err) => {
        handleAxiosError(err, dispatch);
        setUpdateId(-1);
        setUpdateDialogeOpen(false);
      });
  }, [updateId]);

  const changeUpdateData = (e) => {
    setUpdateData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  useEffect(() => {
    if (!createDialogOpen) {
      reset();
      // setNewCustomerData(emptyNewCustomerData);
    }
  }, [createDialogOpen]);

  const [queryFilters, setQueryFilters] = useState({ company_name: "" });
  function onQueryFilterChange({ target }) {
    const { name, value } = target;
    setQueryFilters((prev) => ({ ...prev, [name]: value }));
  }
  const [customerTableRefreshCount, setCustomerTableRefreshCount] = useState(0);
  async function getCustomerTableData(limit, offset) {
    try {
      const { data } = await getCustomers(
        { limit, offset, sort: "creation_date", ...queryFilters },
        user.token
      );
      let filtersApplied = Object.keys(queryFilters).some(
        (k) => queryFilters[k]
      );
      return {
        rows: data.data,
        // total: filtersApplied ? data.data.length : data.total,
        total: data.total,
        error: false,
      };
    } catch (error) {
      handleAxiosError(error, dispatch);
      return { error: true };
    } finally {
      // setLoading(false);
    }
  }
  const [showPassword, setShowPassword] = useState(false);

  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Box className="pmt-container-box">
      <Box className="pmt-title-box">
        <Box className="typography-title">
          <Typography variant="h1" className="pmt-headings-h1">
            Customers
          </Typography>
        </Box>
        <Button
          variant="contained"
          style={{ padding: "5px 7px" }}
          onClick={() => {
            setCreateDialogOpen(true);
          }}
          className="blue-button"
        >
          Add Customer
        </Button>
      </Box>
      <br />
      <Box
        component="form"
        onChange={refreshTable}
        // sx={{ px: 2, pb: 2, display: "flex", columnGap: 1 }}
        className="pmt-filter-box"
      >
        <TextField
          size="small"
          label="Company Name"
          name="company_name"
          value={queryFilters.company_name}
          onChange={onQueryFilterChange}
        />
        {/* <Button variant="contained" size="small" type="submit" >Search</Button> */}
      </Box>
      <div className="pmt-main-table">
        <BetterControlledTable
          hasActionButtons={false}
          getTableRows={getCustomerTableData}
          columns={tableDef}
          perPage={10}
          refreshCounter={customerTableRefreshCount}
        />
      </div>
      <DeleteAlertDialog
        open={deleteDialogOpen}
        setOpen={setDeleteDialogOpen}
        setDeleteId={setDeleteId}
        rowId={dialogAction.data?.id}
      />
      <UpdateDialog
        open={updateDialogOpen}
        setOpen={setUpdateDialogeOpen}
        setUpdateId={setUpdateId}
        rowId={dialogAction.data?.id}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={6}>
            <TextField
              margin="dense"
              name="email"
              fullWidth
              value={updateData.email}
              onChange={changeUpdateData}
              label="Email"
              type="email"
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <TextField
              margin="dense"
              name="firstname"
              fullWidth
              value={updateData.firstname}
              onChange={changeUpdateData}
              label="First Name"
              className="name-textfeild"
              type="text"
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <TextField
              margin="dense"
              name="lastname"
              fullWidth
              value={updateData.lastname}
              onChange={changeUpdateData}
              label="Last Name"
              className="name-textfeild"
              type="text"
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <TextField
              margin="dense"
              name="contact"
              fullWidth
              value={updateData.contact}
              error={!/^[0-9]{10}$/.test(updateData.contact)}
              inputProps={{ maxLength: 10 }}
              onChange={changeUpdateData}
              label="Contact"
              type="tel"
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={12}>
            <TextField
              margin="dense"
              name="address"
              fullWidth
              value={updateData.address}
              onChange={changeUpdateData}
              label="Address"
              type="text"
              variant="outlined"
            />
          </Grid>
        </Grid>
      </UpdateDialog>
      <CreateDialog
        open={createDialogOpen}
        setOpen={setCreateDialogOpen}
        onSubmit={handleSubmit(onNewVendorSubmit)}
      >
        <form onSubmit={handleSubmit(onNewVendorSubmit)}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={6}>
              <TextField
                margin="dense"
                name="company_name"
                fullWidth
                // value={newCustomerData.email}
                // onChange={changeNewCustomerData}
                label="Company Name"
                type="text"
                variant="outlined"
                inputRef={register({
                  required: "Company Name is required.",
                })}
                required
                error={Boolean(errors.company_name)}
                helperText={errors.company_name?.message}
                className="name-textfeild"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <TextField
                margin="dense"
                name="firstname"
                fullWidth
                // value={newCustomerData.firstname}
                // onChange={changeNewCustomerData}
                label="Primay Contact"
                className="name-textfeild"
                type="text"
                variant="outlined"
                inputRef={register({
                  required: "First Name is required.",
                })}
                required
                error={Boolean(errors.firstname)}
                helperText={errors.firstname?.message}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <TextField
                margin="dense"
                name="email"
                fullWidth
                // value={newCustomerData.email}
                // onChange={changeNewCustomerData}
                label="Primary Email"
                type="email"
                variant="outlined"
                inputRef={register({
                  required: "Email Address  is required.",
                  pattern: {
                    value:
                      /^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/,
                    message: "Please enter a valid e-mail address.",
                  },
                })}
                required
                error={Boolean(errors.email)}
                helperText={errors.email?.message}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <TextField
                margin="dense"
                name="password"
                fullWidth
                label="Password"
                type={showPassword ? "text" : "password"} // Toggle between text and password type
                variant="outlined"
                inputRef={register({
                  required: "Password is required.",
                  validate: (value) =>
                    value.length > 4 || "Password is not valid",
                })}
                required
                error={Boolean(errors.password)}
                helperText={errors.password?.message}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={toggleShowPassword} edge="end">
                        {showPassword ? <Visibility /> : <VisibilityOff />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <TextField
                margin="dense"
                name="contact"
                fullWidth
                // value={newCustomerData.contact}
                // error={!/^[0-9]{10}$/.test(newCustomerData.contact)}
                inputProps={{ maxLength: 10 }}
                // onChange={changeNewCustomerData}
                label="Phone Number"
                type="tel"
                variant="outlined"
                inputRef={register({
                  required: "Contact Number is required.",
                  validate: (value) =>
                    value.length > 9 || "Please enter a valid number",
                })}
                required
                error={Boolean(errors.contact)}
                helperText={errors.contact?.message}
              />
            </Grid>
            {/* <Grid item xs={12} sm={6} md={6}>
              <TextField
                margin="dense"
                name="lastname"
                fullWidth
                // value={newCustomerData.lastname}
                // onChange={changeNewCustomerData}
                label="Last Name"
                className="name-textfeild"
                type="text"
                variant="outlined"
                inputRef={register({
                  required: "Last Name is required.",
                })}
                required
                error={Boolean(errors.lastname)}
                helperText={errors.address?.lastname}
              />
            </Grid> */}

            <Grid item xs={12} sm={12} md={12}>
              <TextField
                margin="dense"
                name="address"
                fullWidth
                // value={newCustomerData.address}
                // onChange={changeNewCustomerData}
                label="Address"
                type="text"
                variant="outlined"
                inputRef={register({
                  required: "Address is required.",
                })}
                required
                error={Boolean(errors.address)}
                helperText={errors.address?.message}
              />
            </Grid>
          </Grid>
        </form>
      </CreateDialog>
    </Box>
  );
}

function getUpdatableData(data) {
  return {
    vendor_name: data.vendor_name,
    company_name: data.company_name,
    email: data.email,
    contact: data.contact,
    shipping_address: data.shipping_address,
    billing_address: data.billing_address,
    postal_code: data.postal_code,
    country: data.country,
    city: data.city,
    currency: data.currency,
    state: data.state,
  };
}
